import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ChevronDown,
  Crown,
  Loader2,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import { useMediaQuery } from "../hooks/useMediaQuery";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import BettorBookBoard from "../components/operator/BettorBookBoard";
import BettorDetailPanel from "../components/operator/BettorDetailPanel";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearBetHouseCustomers,
  fetchBetHouseCustomers,
  fetchBettorTickets,
  fetchOperatorDashboard,
} from "../redux/operatorConsoleSlice";
import { formatCash } from "../utils/operatorConsoleHelpers";
import {
  BETTOR_SORTS,
  bettorDateKey,
  formatBettorCount,
  MIN_TICKET_PRESETS,
  ticketsNeedFixtureHydration,
} from "../utils/bettorsDeskHelpers";
import { buildTimelineSections } from "../utils/activityHelpers";
import {
  ROSTER_MOBILE_INITIAL,
  ROSTER_MOBILE_STEP,
  ROSTER_STACK_BREAKPOINT,
  sliceRosterSections,
} from "../utils/rosterMobileHelpers";
import "../bettors-desk.css";

const BettorsDesk = () => {
  const { houseId } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;

  const {
    houses,
    dashboard,
    dashboardHouseId,
    dashboardStatus,
    customers,
    customersPagination,
    customersPulse,
    customersStatus,
    customersError,
    loadingMoreCustomers,
  } = useSelector((s) => s.operatorConsole);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [repeatOnly, setRepeatOnly] = useState(false);
  const [minTickets, setMinTickets] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [ticketCache, setTicketCache] = useState({});
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState(null);
  const [toast, setToast] = useState(null);
  const [rosterVisibleCount, setRosterVisibleCount] = useState(ROSTER_MOBILE_INITIAL);
  const ticketCacheRef = useRef(ticketCache);
  const detailRef = useRef(null);
  const isStackedLayout = useMediaQuery(ROSTER_STACK_BREAKPOINT);

  useEffect(() => {
    ticketCacheRef.current = ticketCache;
  }, [ticketCache]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [toast]);

  const handleRewardSuccess = useCallback(({ currency, amount, bettorName }) => {
    const formatted =
      currency === "COIN"
        ? `${Number(amount).toLocaleString()} Coins`
        : formatCash(amount, { compact: true });
    setToast({
      type: "success",
      message: `${formatted} sent to ${bettorName || "bettor"}.`,
    });
  }, []);

  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );

  const hasActiveFilters = Boolean(debouncedSearch || repeatOnly || minTickets > 0);

  useEffect(() => {
    if (houseId) dispatch(fetchOperatorDashboard(houseId));
  }, [dispatch, houseId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const listParams = useMemo(
    () => ({
      houseId,
      page: 1,
      limit: 20,
      search: debouncedSearch,
      sort,
      repeatOnly,
      minTickets,
      append: false,
    }),
    [houseId, debouncedSearch, sort, repeatOnly, minTickets]
  );

  const reload = useCallback(() => {
    if (!houseId) return;
    dispatch(fetchBetHouseCustomers(listParams));
  }, [dispatch, houseId, listParams]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    return () => dispatch(clearBetHouseCustomers());
  }, [dispatch]);

  const loadTickets = useCallback(
    async (bettorUserId, { force = false } = {}) => {
      if (!houseId || !userId || !bettorUserId) return;
      const key = String(bettorUserId);
      const cached = ticketCacheRef.current[key];
      if (
        cached?.fetched &&
        !force &&
        !ticketsNeedFixtureHydration(cached.tickets)
      ) {
        return;
      }

      setTicketsLoading(true);
      setTicketsError(null);
      try {
        const result = await dispatch(
          fetchBettorTickets({ houseId, bettorUserId: key, operatorUserId: userId })
        ).unwrap();
        setTicketCache((prev) => ({
          ...prev,
          [key]: { fetched: true, tickets: result.tickets || [] },
        }));
      } catch (err) {
        setTicketsError(String(err));
        setTicketCache((prev) => ({
          ...prev,
          [key]: { fetched: true, tickets: [] },
        }));
      } finally {
        setTicketsLoading(false);
      }
    },
    [dispatch, houseId, userId]
  );

  useEffect(() => {
    if (customersStatus !== "succeeded" || customers.length === 0) {
      if (customersStatus === "succeeded" && customers.length === 0) {
        setSelectedId(null);
      }
      return;
    }

    setSelectedId((prev) => {
      if (prev && customers.some((row) => String(row.userId) === prev)) return prev;
      return String(customers[0].userId);
    });
  }, [customers, customersStatus]);

  useEffect(() => {
    if (!selectedId) return;
    loadTickets(selectedId);
  }, [selectedId, loadTickets]);

  const handleSelect = (row) => {
    const id = String(row.userId);
    setSelectedId(id);
    setTicketsError(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setTicketCache({});
    try {
      await Promise.all([
        dispatch(fetchBetHouseCustomers(listParams)).unwrap(),
        dispatch(fetchOperatorDashboard(houseId)).unwrap(),
      ]);
      if (selectedId) {
        await loadTickets(selectedId, { force: true });
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleLoadMore = () => {
    if (!houseId || loadingMoreCustomers || !customersPagination?.hasNextPage) return;
    dispatch(
      fetchBetHouseCustomers({
        houseId,
        page: (customersPagination.page || 1) + 1,
        limit: customersPagination.limit || 20,
        search: debouncedSearch,
        sort,
        repeatOnly,
        minTickets,
        append: true,
      })
    );
  };

  const sections = useMemo(
    () => buildTimelineSections(customers, bettorDateKey),
    [customers]
  );

  useEffect(() => {
    setRosterVisibleCount(ROSTER_MOBILE_INITIAL);
  }, [debouncedSearch, sort, repeatOnly, minTickets]);

  const { displaySections, rosterHiddenCount } = useMemo(() => {
    if (!isStackedLayout) {
      return { displaySections: sections, rosterHiddenCount: 0 };
    }
    return sliceRosterSections(
      sections,
      rosterVisibleCount,
      selectedId,
      (row) => row.userId
    );
  }, [sections, isStackedLayout, rosterVisibleCount, selectedId]);

  useEffect(() => {
    if (!selectedId || !isStackedLayout) return;
    const timer = window.setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [selectedId, isStackedLayout]);

  const selectedBettor = useMemo(
    () => customers.find((r) => String(r.userId) === String(selectedId)),
    [customers, selectedId]
  );

  const selectedTickets = selectedId ? ticketCache[selectedId]?.tickets || [] : [];

  const analyticsLoading =
    customersStatus === "loading" && !customers.length && !customersPulse;

  const pagePulse = useMemo(() => {
    const visibleTickets = customers.reduce((s, r) => s + Number(r.ticketCount || 0), 0);
    const visibleStake = customers.reduce((s, r) => s + Number(r.totalStaked || 0), 0);
    const repeat = customers.filter((r) => Number(r.ticketCount || 0) >= 2).length;
    return { visibleTickets, visibleStake, repeat };
  }, [customers]);

  const dashboardReady =
    dashboardStatus === "succeeded" && String(dashboardHouseId) === String(houseId);

  const topBettors = useMemo(
    () => (dashboardReady ? dashboard?.customers?.topBettors || [] : []),
    [dashboardReady, dashboard]
  );

  // userId → rank (1-based) among the house's top-staked bettors.
  const topBettorRanks = useMemo(() => {
    const ranks = new Map();
    [...topBettors]
      .sort((a, b) => (Number(b.totalStaked) || 0) - (Number(a.totalStaked) || 0))
      .slice(0, 5)
      .forEach((b, i) => ranks.set(String(b.userId), i + 1));
    return ranks;
  }, [topBettors]);

  const pulse = useMemo(() => {
    const matched = customersPagination?.totalCount ?? customers.length;

    if (hasActiveFilters) {
      return {
        bettors: matched,
        tickets: pagePulse.visibleTickets,
        stake: pagePulse.visibleStake,
        repeat: pagePulse.repeat,
      };
    }

    return {
      bettors: dashboardReady
        ? dashboard?.customers?.totalUnique ?? matched
        : matched,
      tickets: dashboardReady
        ? dashboard?.activity?.totalTickets ?? pagePulse.visibleTickets
        : pagePulse.visibleTickets,
      stake: dashboardReady
        ? dashboard?.activity?.totalStakesReceived ?? pagePulse.visibleStake
        : pagePulse.visibleStake,
      repeat: dashboardReady
        ? dashboard?.customers?.repeatBettors ?? pagePulse.repeat
        : pagePulse.repeat,
    };
  }, [
    hasActiveFilters,
    customersPagination?.totalCount,
    customers.length,
    pagePulse,
    dashboardReady,
    dashboard,
  ]);

  const headerSubtitle = useMemo(() => {
    const houseName = bet?.name || "House";
    if (customersStatus === "loading" && !customers.length) {
      return houseName;
    }
    const count = hasActiveFilters
      ? customersPagination?.totalCount ?? customers.length
      : pulse.bettors;
    return `${houseName} · ${formatBettorCount(count)}`;
  }, [
    bet?.name,
    customersStatus,
    customers.length,
    hasActiveFilters,
    customersPagination?.totalCount,
    pulse.bettors,
  ]);

  if (!houseId) {
    return (
      <OperatorConsoleShell activeSection="bettors" mainClassName="oc-desk-main">
        <div className="bd-page bd-customers-desk">
          <div className="bd-loading">Missing house.</div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="bettors" mainClassName="oc-desk-main">
      <div className="bd-page bd-customers-desk">
      <header className="bd-header bd-customers-header">
        <div className="bd-header-copy">
          <p className="bd-eyebrow">
            <Users size={9} />
            Customer desk
          </p>
          <span className="bd-header-title">
            {headerSubtitle}
            {customersStatus === "succeeded" && pulse.tickets > 0
              ? ` · ${pulse.tickets} tickets · ${formatCash(pulse.stake, { compact: true })} staked`
              : ""}
          </span>
        </div>
        <button
          type="button"
          className="bd-icon-btn"
          onClick={handleRefresh}
          disabled={refreshing || customersStatus === "loading"}
          aria-label="Refresh"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
        </button>
      </header>

      <div className="bd-desk-body">
      {toast ? (
        <div className={`bd-toast bd-toast-${toast.type || "success"}`} role="status">
          {toast.message}
        </div>
      ) : null}

      <div className="bd-bettors-command">
        <aside className="bd-roster-rail">
          <div className="bd-roster-toolbar">
            <div className="bd-search bd-search-compact">
              <Search size={13} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search bettors…"
                aria-label="Search bettors"
              />
              {search ? (
                <button
                  type="button"
                  className="bd-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear"
                >
                  <X size={13} />
                </button>
              ) : null}
            </div>

            <div className="bd-chip-row bd-chip-row-compact" role="group" aria-label="Sort">
              {BETTOR_SORTS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`bd-chip ${sort === value ? "bd-chip-active" : ""}`}
                  onClick={() => setSort(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="bd-chip-row bd-chip-row-compact" role="group" aria-label="Filters">
              <button
                type="button"
                className={`bd-chip ${repeatOnly ? "bd-chip-active" : ""}`}
                onClick={() => setRepeatOnly((v) => !v)}
              >
                Repeat
              </button>
              {MIN_TICKET_PRESETS.filter((p) => p.value > 0).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`bd-chip ${minTickets === value ? "bd-chip-active" : ""}`}
                  onClick={() => setMinTickets((prev) => (prev === value ? 0 : value))}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <section className="bd-roster bd-roster-rail-scroll">
            <header className="bd-rail-head">
              <h2>Roster</h2>
              <span className="tabular-nums">
                {hasActiveFilters
                  ? customersPagination?.totalCount ?? customers.length
                  : pulse.bettors}
              </span>
            </header>

            {customersStatus === "loading" && !customers.length && (
              <div className="bd-loading bd-loading-compact">
                <Loader2 size={18} className="animate-spin" />
                <span>Loading…</span>
              </div>
            )}

            {customersError && !customers.length && (
              <div className="bd-loading bd-loading-compact">
                <p>{String(customersError)}</p>
                <button type="button" onClick={reload}>
                  Retry
                </button>
              </div>
            )}

            {!customers.length && customersStatus === "succeeded" && (
              <p className="bd-empty bd-empty-compact">No bettors match.</p>
            )}

            {displaySections.map((section) => (
              <div key={section.dateKey} className="bd-day">
                <h3 className="bd-day-head">
                  <span>{section.title}</span>
                  <span className="bd-day-line" aria-hidden />
                </h3>
                <ul className="bd-roster-list">
                  {section.data.map((row) => {
                    const id = String(row.userId);
                    const active = selectedId === id;
                    const isRepeat = Number(row.ticketCount || 0) >= 2;
                    const rank = topBettorRanks.get(id) || null;
                    const isCrowned = rank != null && rank <= 3;
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          className={`bd-roster-row ${rank ? "bd-roster-row-vip" : ""} ${
                            active ? "bd-roster-row-active" : ""
                          }`}
                          onClick={() => handleSelect(row)}
                        >
                          <span
                            className={`bd-roster-avatar ${isCrowned ? "bd-roster-avatar-vip" : ""}`}
                          >
                            {row.username?.[0]?.toUpperCase() || "?"}
                          </span>
                          <div className="bd-roster-copy">
                            <strong>
                              {isCrowned ? (
                                <Crown
                                  size={10}
                                  className="bd-roster-crown"
                                  aria-label={`Top ${rank} bettor`}
                                />
                              ) : null}
                              {row.username || "Bettor"}
                            </strong>
                            <span>
                              {row.ticketCount} tix
                              {isRepeat ? <em className="bd-roster-repeat">Repeat</em> : null}
                            </span>
                          </div>
                          <div className="bd-roster-side">
                            <em className={`tabular-nums ${rank ? "bd-roster-stake-vip" : ""}`}>
                              {formatCash(row.totalStaked, { compact: true })}
                            </em>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {rosterHiddenCount > 0 ? (
              <button
                type="button"
                className="bd-roster-load-more"
                onClick={() =>
                  setRosterVisibleCount((count) => count + ROSTER_MOBILE_STEP)
                }
              >
                <ChevronDown size={13} aria-hidden />
                Show more
                <span className="tabular-nums">{rosterHiddenCount}</span>
              </button>
            ) : null}

            {customersPagination?.hasNextPage ? (
              <button
                type="button"
                className="bd-load-more bd-load-more-compact"
                onClick={handleLoadMore}
                disabled={loadingMoreCustomers}
              >
                {loadingMoreCustomers ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    Loading…
                  </>
                ) : (
                  "Load more"
                )}
              </button>
            ) : null}
          </section>
        </aside>

        <div className="bd-bettors-workspace">
          <BettorBookBoard
            layout="workspace"
            pulseRaw={customersPulse}
            loading={analyticsLoading}
            filtered={hasActiveFilters}
            topBettors={topBettors}
            houseHandle={
              dashboardReady ? dashboard?.activity?.totalStakesReceived ?? null : null
            }
            rows={customers}
            hasMorePages={Boolean(customersPagination?.hasNextPage)}
          />

          <div ref={detailRef} className="bd-detail-wrap">
            <BettorDetailPanel
              bettor={selectedBettor}
              tickets={selectedTickets}
              ticketsLoading={
                ticketsLoading && !!selectedId && !ticketCache[selectedId]?.fetched
              }
              ticketsError={ticketsError}
              houseId={houseId}
              houseName={bet?.name || dashboard?.houseName}
              operatorUserId={userId}
              onRewardSuccess={handleRewardSuccess}
            />
          </div>
        </div>
      </div>
      </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default BettorsDesk;
